import { useEffect, useState } from "react"
import { API_BASE_URL } from "../config"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from "recharts"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { fetchMetrics, fetchExportJobs, requestExport } from "@/lib/reportsApi"
import { getToken } from "@/lib/auth"
import { DownloadIcon, RefreshCwIcon, FileTextIcon, ClockIcon, ActivityIcon, CheckCircleIcon, AlertCircleIcon } from "lucide-react"

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#8dd1e1', '#a4de6c', '#d0ed57', '#ffc658'];

const EXPORT_FIELDS = [
  "ID de Seguimiento",
  "Fecha",
  "Tipo de Beneficio",
  "Nombre del Afiliado",
  "Identificación del Afiliado",
  "Estado",
  "Resolución",
  "Fecha de Resolución"
]

export function ReportsDashboard() {
  const [metrics, setMetrics] = useState<any>(null)
  const [jobs, setJobs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [loadingJobs, setLoadingJobs] = useState(true)
  const [selectedFields, setSelectedFields] = useState<string[]>(EXPORT_FIELDS)
  const [requesting, setRequesting] = useState(false)

  async function loadData() {
    const token = getToken()
    if (!token) return
    try {
      setLoading(true)
      setError("")
      const data = await fetchMetrics(token)
      setMetrics(data)
    } catch (err) {
      console.error(err)
      setError("Error al cargar los reportes. Verifica que tienes permisos de administrador.")
    } finally {
      setLoading(false)
    }
  }

  async function loadJobs() {
    const token = getToken()
    if (!token) return
    try {
      setLoadingJobs(true)
      const data = await fetchExportJobs(token)
      setJobs(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingJobs(false)
    }
  }

  useEffect(() => {
    loadData()
    loadJobs()
  }, [])

  function toggleField(field: string) {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter(f => f !== field))
    } else {
      setSelectedFields([...selectedFields, field])
    }
  }

  async function handleExport() {
    const token = getToken()
    if (!token) return
    if (selectedFields.length === 0) {
      alert("Selecciona al menos un campo para exportar.")
      return
    }
    
    try {
      setRequesting(true)
      await requestExport(token, selectedFields)
      alert("Trabajo de exportación iniciado. Aparecerá en la tabla de trabajos a continuación.")
      loadJobs()
    } catch (err) {
      alert("No se pudo iniciar la exportación.")
    } finally {
      setRequesting(false)
    }
  }

  if (error) {
    return <div className="p-8 text-center text-destructive flex items-center justify-center">
      <AlertCircleIcon className="mr-2 size-5" />
      {error}
    </div>
  }

  if (loading || !metrics) {
    return <div className="p-8 text-center text-muted-foreground flex items-center justify-center">
      <RefreshCwIcon className="animate-spin mr-2" /> Cargando reportes...
    </div>
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Solicitudes</CardTitle>
            <FileTextIcon className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.total_requests}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tiempo Resp. (Días)</CardTitle>
            <ClockIcon className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.avg_response_time_days}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aprobadas</CardTitle>
            <CheckCircleIcon className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{metrics.approved_count}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Denegadas</CardTitle>
            <ActivityIcon className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{metrics.denied_count}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Solicitudes por Tipo de Beneficio</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metrics.requests_by_type} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="benefit_name" tick={{fontSize: 12}} />
                <YAxis allowDecimals={false} />
                <RechartsTooltip cursor={{fill: 'transparent'}} />
                <Bar dataKey="count" name="Solicitudes" radius={[4, 4, 0, 0]}>
                  {
                    metrics.requests_by_type.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))
                  }
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Generar Exportación CSV</CardTitle>
            <CardDescription>Selecciona los campos y genera un archivo CSV con las solicitudes activas.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {EXPORT_FIELDS.map(field => (
                <div key={field} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`field-${field}`} 
                    checked={selectedFields.includes(field)}
                    onCheckedChange={() => toggleField(field)}
                  />
                  <label htmlFor={`field-${field}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {field}
                  </label>
                </div>
              ))}
            </div>
            <Button className="w-full" onClick={handleExport} disabled={requesting || selectedFields.length === 0}>
              <DownloadIcon className="mr-2 size-4" />
              {requesting ? "Procesando..." : "Generar Exportación"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Historial de Exportaciones</CardTitle>
              <CardDescription>Trabajos de exportación recientes</CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={loadJobs} disabled={loadingJobs}>
              <RefreshCwIcon className={`size-4 ${loadingJobs ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent>
            {jobs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No hay exportaciones recientes.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Archivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map(job => (
                    <TableRow key={job.id}>
                      <TableCell className="text-xs">{job.created_at}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          job.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                          job.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {job.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {job.status === 'COMPLETED' && job.file_url && (
                          <a href={`${API_BASE_URL}${job.file_url}`} target="_blank" rel="noreferrer" download>
                            <Button variant="outline" size="sm">Descargar</Button>
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
